import { api } from '@/lib/api-client'
import { downloadFile } from '@/lib/download'
import type { IssueSeverity } from '@/shared/constants/issue-status'

import { mapIssuesReport, mapOfficerPerformance, mapSurveysReport, type RawIssuesResponse, type RawOfficerPerfResponse, type RawSurveysResponse } from './map-reports'
import { reportsScopeParams } from '../lib/reports-scope'

import type { GisScope, IssueReportGroup, IssuesReportPage, OfficerPerfPage, SurveyReportStatus, SurveysReportPage } from './types'

/** Page size for on-screen report tables. The full set is the xlsx export, not pagination. */
const PAGE_SIZE = 50

/** Filters applied to a report: the panel scope + the optional selected mandal. */
export interface ReportFilters {
  scope: GisScope
  mandal?: string | undefined
}

const OFFICER_PERF_URL = '/reports/officer-performance'

export async function getOfficerPerformance({ scope, mandal }: ReportFilters, offset = 0): Promise<OfficerPerfPage> {
  const raw = await api.get<RawOfficerPerfResponse>(OFFICER_PERF_URL, {
    params: { ...reportsScopeParams(scope, { mandal }), format: 'json', limit: PAGE_SIZE, offset },
    suppressErrorToast: true,
  })
  return mapOfficerPerformance(raw)
}

export async function exportOfficerPerformance({ scope, mandal }: ReportFilters): Promise<void> {
  await downloadFile(OFFICER_PERF_URL, 'officer-performance.xlsx', {
    params: { ...reportsScopeParams(scope, { mandal }), format: 'xlsx' },
  })
}

const SURVEYS_URL = '/reports/surveys'

export async function getSurveysReport(filters: ReportFilters, status: SurveyReportStatus, offset = 0): Promise<SurveysReportPage> {
  const raw = await api.get<RawSurveysResponse>(SURVEYS_URL, {
    params: { ...reportsScopeParams(filters.scope, { mandal: filters.mandal }), status, format: 'json', limit: PAGE_SIZE, offset },
    suppressErrorToast: true,
  })
  return mapSurveysReport(raw)
}

export async function exportSurveysReport(filters: ReportFilters, status: SurveyReportStatus): Promise<void> {
  await downloadFile(SURVEYS_URL, `surveys-${status}.xlsx`, {
    params: { ...reportsScopeParams(filters.scope, { mandal: filters.mandal }), status, format: 'xlsx' },
  })
}

const ISSUES_URL = '/reports/issues'

/**
 * Scope + status_group + (optional) severity params shared by the issues report's JSON fetch and its
 * xlsx export, so the two can never drift. `format`/`limit`/`offset` are added by each caller.
 * `severity` (low|medium|critical) is omitted entirely when unset so the endpoint returns all severities.
 */
/** Export accepts the combined `open` group (= pending + active) in addition to the 3 report groups. */
export type IssueExportGroup = IssueReportGroup | 'open'

export function issuesReportFilterParams(
  filters: ReportFilters,
  group: IssueExportGroup,
  severity?: IssueSeverity,
): Record<string, string> {
  return {
    ...reportsScopeParams(filters.scope, { mandal: filters.mandal }),
    status_group: group,
    ...(severity ? { severity } : {}),
  }
}

export async function getIssuesReport(
  filters: ReportFilters,
  group: IssueReportGroup,
  severity?: IssueSeverity,
  offset = 0,
): Promise<IssuesReportPage> {
  const raw = await api.get<RawIssuesResponse>(ISSUES_URL, {
    params: { ...issuesReportFilterParams(filters, group, severity), format: 'json', limit: PAGE_SIZE, offset },
    suppressErrorToast: true,
  })
  return mapIssuesReport(raw)
}

export async function exportIssuesReport(filters: ReportFilters, group: IssueExportGroup, severity?: IssueSeverity): Promise<void> {
  await downloadFile(ISSUES_URL, `issues-${group}${severity ? `-${severity}` : ''}.xlsx`, {
    params: { ...issuesReportFilterParams(filters, group, severity), format: 'xlsx' },
  })
}

export { PAGE_SIZE }
